import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppContext } from 'Context/ReactAppContext';
import { getFromLocalStorage, remove, setInLocalStorage } from 'Client/local-storage-api';
import {
  SHOPPING_FAVORITE_PRODUCTS,
  SHOPPING_FAVORITE_PRODUCTS_NUMBER_MAX,
  SHOPPING_REMOTE_FAVORITE_PRODUCTS,
} from 'Tracking/constants';
import { useAuthentication } from 'Src/hooks/use-authentication/useAuthentication';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import {
  CUSTOMER_ACCOUNT_GET_BOOKMARKS,
  CUSTOMER_ACCOUNT_GET_SEARCHES,
} from 'Utils/request/requestDictionary';
import {
  addBookmarkMutation,
  deleteBookmarkMutation,
  getBookmarksQuery,
  mapBookmarkEntity,
  mapSearchEntity,
} from 'Components/widgets/price-remote/price-remote-favorite/favorite.utils';
import { getUserSearchesQuery } from 'Components/utils/booking/bookingSearch.queries';
import { difference, differenceWith, partition } from 'lodash';
import type { FavoriteMapperInput } from 'Src/types/server-helpers';
import type { FavoriteProductType } from './FavoriteProductListContext';
import { FavoriteProductListContext } from './FavoriteProductListContext';

type Props = {
  children: ReactNode;
};

const FavoriteProductListProvider = ({ children }: Props) => {
  const { isLogged } = useAuthentication();
  const appContext = useAppContext();
  const localStorageFavoritesName = isLogged
    ? SHOPPING_REMOTE_FAVORITE_PRODUCTS
    : SHOPPING_FAVORITE_PRODUCTS;
  const [favoriteProductsState, setFavoriteProductsState] = useState({
    favoriteProductList: [],
    showcaseLatestFavorite: false,
    showAllFavorite: false,
    hasTooManyFavorites: false,
  });
  const [loading, setLoading] = useState(true);
  const [travelPlan, setTravelPlan] = useState<FavoriteProductType | null>(null);

  // Hydrate from localStorage client-side only — localStorage doesn't exist on
  // the server, so initialising from it directly causes a hydration mismatch (#418).
  useEffect(() => {
    const localFavoriteProductsList = getFromLocalStorage(localStorageFavoritesName) || [];
    if (localFavoriteProductsList.length > 0) {
      setFavoriteProductsState((prev) => ({
        ...prev,
        favoriteProductList: localFavoriteProductsList,
        hasTooManyFavorites:
          localFavoriteProductsList.length >= SHOPPING_FAVORITE_PRODUCTS_NUMBER_MAX,
      }));
    }
  }, [localStorageFavoritesName]);

  const setHasTooManyFavorites = useCallback(
    (hasTooManyFavorites: boolean) =>
      setFavoriteProductsState((prev) => ({ ...prev, hasTooManyFavorites })),
    [],
  );

  const setFavoriteProductList = useCallback(
    (
      favoriteProductList: Array<FavoriteProductType>,
      showcaseLatestFavorite: boolean,
      showAllFavorite: boolean,
    ) => {
      favoriteProductList.length
        ? setInLocalStorage(localStorageFavoritesName, favoriteProductList)
        : remove(localStorageFavoritesName);
      setFavoriteProductsState((prev) => ({
        ...prev,
        favoriteProductList,
        showcaseLatestFavorite,
        showAllFavorite,
        hasTooManyFavorites: favoriteProductList.length >= SHOPPING_FAVORITE_PRODUCTS_NUMBER_MAX,
      }));
    },
    [localStorageFavoritesName],
  );

  const addTravelPlan = useCallback((proposal: Proposal, product: Product) => {
    setTravelPlan(
      mapSearchEntity({
        adults: proposal.attendees?.adults?.length,
        birthdates: (proposal.attendees?.children || []).map(({ birthdate }) => birthdate),
        returnDate: proposal.departureDate,
        departureDate: proposal.arrivalDate,
        departureCity: proposal.transports?.inbound?.travelSections?.[0]?.departure?.cityId || 'NO',
        price: proposal.price,
        reference: proposal.id,
        resort: product,
        isBookable: undefined,
        type: undefined,
        upsells: undefined,
      }) as FavoriteProductType,
    );
  }, []);

  const syncLocalAndRemoteFavorites = useCallback(
    async (
      remoteBookmarks: FavoriteProductType[],
      localBookmarks: FavoriteProductType[] | null,
      searches: FavoriteProductType[],
    ) => {
      const bookmarksToSync = differenceWith(
        localBookmarks || [],
        [...remoteBookmarks, ...searches],
        (item1, item2) =>
          item1.adultsNumber === item2.adultsNumber &&
          item1.departureDate === item2.departureDate &&
          item1.duration === item2.duration &&
          item1.departureCity === item2.departureCity &&
          (item1.kids?.length ?? 0) === (item2.kids?.length ?? 0) &&
          item1.product?.id === item2.product?.id,
      );

      let bookmarks = [];
      try {
        bookmarks = await Promise.all(
          bookmarksToSync.map((bookmark) => addBookmarkMutation(bookmark, appContext)),
        );
      } catch {
        // Sync failed — fall back to remote bookmarks so the page still renders.
        setFavoriteProductList([...remoteBookmarks, ...searches], false, false);
        return;
      }

      if (bookmarksToSync.length) {
        setInLocalStorage(SHOPPING_FAVORITE_PRODUCTS, difference(localBookmarks, bookmarksToSync));
      }

      //Update the list of favorites with products received from ADD resource or from GET resource
      const favoriteProductList = bookmarks[bookmarks.length - 1]
        ? (bookmarks[bookmarks.length - 1]?.addBookmark || []).map((b) => mapBookmarkEntity(b))
        : remoteBookmarks;
      setFavoriteProductList([...favoriteProductList, ...searches], false, false);
    },
    [appContext, setFavoriteProductList],
  );

  const value = useMemo(
    () => ({
      favoriteProductsState,
      loading,
      addTravelPlan,
      setHasTooManyFavorites,
      setFavoriteProductList,
    }),
    [favoriteProductsState, loading, addTravelPlan, setHasTooManyFavorites, setFavoriteProductList],
  );

  const { isLoading: bookmarksLoading, data: bookmarksData = [] } = useGQL({
    query: `query {
      bookmarks {
        ${getBookmarksQuery(appContext)}
      }
    }`,
    queryOptions: {
      requestName: CUSTOMER_ACCOUNT_GET_BOOKMARKS,
    },
    cache: false,
    ignore: !isLogged,
    map: ({ bookmarks }) => bookmarks,
  });

  const { isLoading: searchesLoading, data: searchesData = [] } = useGQL({
    query: getUserSearchesQuery(appContext.locale),
    queryOptions: {
      requestName: CUSTOMER_ACCOUNT_GET_SEARCHES,
    },
    cache: false,
    ignore: !isLogged,
    map: ({ userSearches }) => userSearches,
  });

  useEffect(() => {
    setLoading(bookmarksLoading || searchesLoading);
  }, [bookmarksLoading, searchesLoading]);

  useEffect(() => {
    if (bookmarksLoading || searchesLoading) {
      return;
    }

    const remoteBookmarks = bookmarksData.map(mapBookmarkEntity);
    const searches = searchesData.map(mapSearchEntity);

    const isSameTripAsTravelPlan = (item) =>
      item.adultsNumber === travelPlan?.adultsNumber &&
      item.departureDate === travelPlan?.departureDate &&
      item.duration === travelPlan?.duration &&
      item.departureCity === travelPlan?.departureCity &&
      (item.kids?.length ?? 0) === (travelPlan?.kids?.length ?? 0) &&
      item.product?.id === travelPlan?.product?.id;

    const localBookmarks = favoriteProductsState.favoriteProductList.filter(
      ({ origin }) => origin === 'BOOKMARK',
    );
    const [bookmarksToDelete, bookmarksAfterTravelPlan] = partition(
      localBookmarks,
      isSameTripAsTravelPlan,
    );

    bookmarksToDelete.forEach(({ id }) => deleteBookmarkMutation(id, appContext));

    syncLocalAndRemoteFavorites(
      travelPlan ? bookmarksAfterTravelPlan : remoteBookmarks,
      getFromLocalStorage(SHOPPING_FAVORITE_PRODUCTS),
      travelPlan ? [...searches, travelPlan] : searches,
    );
  }, [bookmarksLoading, searchesLoading, travelPlan, syncLocalAndRemoteFavorites]);

  return (
    <FavoriteProductListContext.Provider value={value}>
      {children}
    </FavoriteProductListContext.Provider>
  );
};

export default FavoriteProductListProvider;
